import type { AdminArea } from '@ft/shared';
import { useCallback, useState } from 'react';
import { useI18n } from '../../i18n';
import { api } from '../../lib/http';
import { Loading, Modal, useLoad, useToast } from '../ui';

/** Bereiche anlegen und umbenennen. Zuordnung der Personen passiert unter „Personen“. */
export function AreasAdmin() {
  const { t } = useI18n();
  const { data, error, reload } = useLoad<AdminArea[]>('/api/admin/areas');
  const [form, setForm] = useState<{ area: AdminArea | null } | null>(null);
  const [toast, say] = useToast();

  return (
    <>
      <div className="row" style={{ alignItems: 'flex-start' }}>
        <p className="muted lead" style={{ flex: 1 }}>{t('areas.lead')}</p>
        <button className="btn primary" onClick={() => setForm({ area: null })}>{t('areas.add')}</button>
      </div>
      {!data ? <Loading error={error} /> : (
        <div className="box tablewrap">
          <table>
            <thead><tr><th>{t('areas.col.name')}</th><th className="r">{t('areas.col.members')}</th><th className="r">{t('areas.col.projects')}</th></tr></thead>
            <tbody>
              {data.map((a) => (
                <tr key={a.id} className="click" tabIndex={0} onClick={() => setForm({ area: a })} onKeyDown={(e) => { if (e.key === 'Enter') setForm({ area: a }); }}>
                  <td><b style={{ fontWeight: 600 }}>{a.name}</b></td>
                  <td className="r num">{a.members}</td>
                  <td className="r num">{a.projects}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {form && <AreaForm area={form.area} onClose={() => setForm(null)} onSaved={(created) => { setForm(null); say(t('af.saved')); if (created) location.reload(); else reload(); }} />}
      {toast}
    </>
  );
}

function AreaForm({ area, onClose, onSaved }: { area: AdminArea | null; onClose: () => void; onSaved: (created: boolean) => void }) {
  const { t } = useI18n();
  const [name, setName] = useState(area?.name ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const close = useCallback(() => { if (!busy) onClose(); }, [busy, onClose]);

  async function save() {
    if (!name.trim()) return setError(t('af.err.name'));
    setBusy(true);
    setError(null);
    try {
      if (area) await api('PUT', `/api/admin/areas/${area.id}`, { version: area.version, name: name.trim() });
      else await api('POST', '/api/admin/areas', { name: name.trim() });
      onSaved(!area);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }

  return (
    <Modal title={area ? t('af.edit', { n: area.name }) : t('af.new')} crumb={t('admin.title')} onClose={close} width={480}
      footer={<>
        {error && <p className="err" role="alert">{error}</p>}
        <button className="btn" onClick={close} disabled={busy}>{t('common.cancel')}</button>
        <button className="btn primary" onClick={save} disabled={busy}>{busy ? t('common.saving') : t('common.save')}</button>
      </>}>
      <form className="form" onSubmit={(e) => { e.preventDefault(); void save(); }}>
        <div className="fld full">
          <label htmlFor="af-n">{t('af.name')} <span className="req">*</span></label>
          <input id="af-n" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('af.namePh')} autoComplete="off" />
          {!area && <span className="help">{t('af.hint')}</span>}
        </div>
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
